import React, { FC, useEffect, useMemo, useState } from "react";
import { Section } from "components/Layout";
import DocumentTitle from "react-document-title";
import { UserSeries } from "redux/state";
import { Tooltip, IconButton } from "@xapp/components";
import { PageHeader } from "components/PageHeader";
import { AppAnalyticsContainer } from "../components/AppAnalyticsContainer";
import { Data } from "./MeasureGraph";

import { DatePickerWrapper } from "components/DatePickerWrapper";
import { format, subDays, subWeeks } from 'date-fns';
import style from "./style.scss";
import { useExportAnalyticsAsCsvQuery } from "generated/graphql";

export interface UsersProps {
    readonly appId: string;
    readonly organizationId?: string;

}

export const emptyUserSeries: UserSeries = {
    intervals: [],
    total: {
        newUsers: undefined,
        returningUsers: undefined,
        totalSessions: undefined,
        totalUsers: undefined
    }
};

export function convertData(appId: string, analytics: UserSeries = emptyUserSeries): Data[] {
    if (analytics.intervals.length === 0) {
        return [];
    }

    const appData: Data[] = analytics.intervals.map((interval) => {
        return {
            appId: appId,
            date: new Date(interval.isoDate).toLocaleDateString(),
            newUsers: interval.total.newUsers,
            returningUsers: interval.total.returningUsers,
            total: interval.total.totalUsers
        };
    });

    return appData;
}

// TODO: This is pretty much a container, should we call it as such and get rid of the UserContainer
export const Users: FC<UsersProps> = ({ appId, organizationId }: UsersProps) => {

    const now = useMemo(() => new Date(), []);
    const [start, setStart] = useState<Date>(subWeeks(now, 1));
    const [end, setEnd] = useState<Date>(now);


    const minDate = useMemo(() => subDays(now, 90), [now]);
    const maxDate = now;

    const disabledDays = [
        { after: new Date(maxDate) },
        { before: new Date(minDate) }
    ];

    const formattedDateRange = `${format(start, 'yyyy/MM/dd')} - ${format(end, 'yyyy/MM/dd')}`;

    const [{ data }, executeQuery] = useExportAnalyticsAsCsvQuery({
        pause: true,
        variables: {
            appId,
            start: start.toISOString(),
            end: end.toISOString()
        }
    });

    useEffect(() => {
        const url = data?.app?.analytics?.user?.csv?.file;
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    }, [data])


    function renderTotals() {
        return <AppAnalyticsContainer startDate={start} endDate={end} appId={appId} organizationId={organizationId} />;
    }

    const handleDateSubmit = (start: Date, end: Date) => {
        setStart(start);
        setEnd(end);
    };

    return (
        <DocumentTitle title={`XAPP | ${appId} - Analytics`}>
            <Section>

                <PageHeader title="Analytics">
                    <div className={style.picker}>
                        <DatePickerWrapper
                            mode="range"
                            dateValue={formattedDateRange}
                            onSubmitDate={handleDateSubmit}
                            disabled={disabledDays}
                        />
                    </div>
                    <div className={style.picker}>
                        <Tooltip content="Export CSV">
                            <IconButton onClick={executeQuery} iconName="file_download" />
                        </Tooltip>
                    </div>
                </PageHeader>

                {renderTotals()}
            </Section>
        </DocumentTitle>
    );
};